import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Bell, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface NotificationPreference {
  inApp: boolean;
  email: boolean;
}

interface NotificationPreferences {
  [key: string]: NotificationPreference;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  TASK_DUE: { inApp: true, email: false },
  TASK_OVERDUE: { inApp: true, email: true },
  LEAD_ASSIGNED: { inApp: true, email: true },
  LEAD_STAGE_CHANGED: { inApp: true, email: false },
  DEAL_ROTTING: { inApp: true, email: false },
  IMPORT_COMPLETE: { inApp: true, email: false },
  SYSTEM_ALERT: { inApp: true, email: true }
};

const NOTIFICATION_TYPES = [
  { key: 'TASK_DUE', label: 'Task due reminders', description: 'Get notified when tasks are approaching their due date' },
  { key: 'TASK_OVERDUE', label: 'Overdue task alerts', description: 'Get alerted about tasks that are past their due date' },
  { key: 'LEAD_ASSIGNED', label: 'Lead assignments', description: 'When a lead is assigned to you' },
  { key: 'LEAD_STAGE_CHANGED', label: 'Lead stage changes', description: 'When a lead moves to a different pipeline stage' },
  { key: 'DEAL_ROTTING', label: 'Stale deal alerts', description: 'When deals have been inactive for too long' },
  { key: 'IMPORT_COMPLETE', label: 'Import completion', description: 'When data imports finish processing' },
  { key: 'SYSTEM_ALERT', label: 'System alerts', description: 'Important system messages and updates' }
];

export const NotificationPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();
  const { toast } = useToast();

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.preferences && typeof data.preferences === 'object') {
          setPreferences({ ...DEFAULT_PREFERENCES, ...data.preferences });
        }
      } else if (response.status === 404) {
        // Preferences not set yet, use defaults
        setPreferences(DEFAULT_PREFERENCES);
      } else {
        throw new Error('Failed to fetch preferences');
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (type: string, channel: 'inApp' | 'email') => {
    if (!token) return;

    const newPrefs = {
      ...preferences,
      [type]: {
        ...preferences[type],
        [channel]: !preferences[type][channel]
      }
    };

    setPreferences(newPrefs);
    setIsSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preferences: newPrefs })
      });

      if (response.ok) {
        toast({
          title: 'Preferences Updated',
          description: 'Your notification preferences have been saved'
        });
      } else {
        throw new Error('Failed to save preferences');
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      // Revert on error
      setPreferences(preferences);
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-600" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-600" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-500">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-orange-600" />
          Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b">
                <th className="pb-3 font-medium">Notification Type</th>
                <th className="pb-3 text-center font-medium">In-App</th>
                <th className="pb-3 text-center font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {NOTIFICATION_TYPES.map(({ key, label, description }) => (
                <tr key={key} className="border-b last:border-b-0">
                  <td className="py-4">
                    <div>
                      <p className="font-medium text-gray-900">{label}</p>
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <button
                      onClick={() => handleToggle(key, 'inApp')}
                      disabled={isSaving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        (preferences[key]?.inApp ?? false) ? 'bg-blue-600' : 'bg-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      aria-label={`Toggle in-app notifications for ${label}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          (preferences[key]?.inApp ?? false) ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="py-4 text-center">
                    <button
                      onClick={() => handleToggle(key, 'email')}
                      disabled={isSaving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        (preferences[key]?.email ?? false) ? 'bg-blue-600' : 'bg-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      aria-label={`Toggle email notifications for ${label}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          (preferences[key]?.email ?? false) ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isSaving && (
          <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving preferences...
          </div>
        )}
      </CardContent>
    </Card>
  );
};
